import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Badge } from '@/components/ui/badge';
import {
  Shield,
  Rocket,
  IndianRupee,
  Building2,
  Zap,
  Bot,
} from 'lucide-react';

const benefits = [
  {
    icon: Shield,
    title: 'No Business API Required',
    description: 'Built on WhatsApp open-source APIs. Skip the complex Business API approval process entirely.',
  },
  {
    icon: Rocket,
    title: 'Easy Setup',
    description: 'Go live in under 5 minutes. Just 2 steps — set up your agent and upload your knowledge base.',
  },
  {
    icon: IndianRupee,
    title: 'Affordable Pricing',
    description: 'Plans designed for Indian SMBs. Start free and scale without breaking the bank.',
  },
  {
    icon: Building2,
    title: 'Built for Indian SMBs',
    description: 'Designed specifically for the needs of small and medium businesses in India.',
  },
  {
    icon: Zap,
    title: 'Fast Deployment',
    description: 'From signup to live AI agent in minutes. No waiting, no technical hurdles.',
  },
  {
    icon: Bot,
    title: 'AI-Powered Responses',
    description: 'Smart, contextual replies powered by advanced AI. Your customers get instant, accurate answers.',
  },
];

export const Benefits = () => {
  const [ref, isVisible] = useScrollAnimation(0.1);

  return (
    <section id="benefits" ref={ref} className="py-24 lg:py-32 relative">
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-accent/[0.03] blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="accent" className="mb-4">
              Why Choose Agentick
            </Badge>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight"
          >
            Everything Your Business Needs
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base lg:text-lg text-muted-foreground font-body leading-relaxed"
          >
            Powerful features, zero complexity. Agentick gives you the tools to automate customer conversations at a fraction of the usual cost.
          </motion.p>
        </div>

        {/* Benefits Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, i) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
              className="group"
            >
              <div className="glass-card rounded-2xl p-7 h-full flex flex-col hover:border-primary/15 transition-all duration-500">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/15 transition-colors duration-300">
                  <benefit.icon size={20} className="text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed font-body">
                  {benefit.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
